/* eslint-disable no-console */
import { MessageType, SystemOptionType, PredefinedOptionType } from '../enum';
import { EventType, ItemType, buildArticleOption, buildConnectConfig, canConnectToAgent, createMessageHistory, generateUserSideArticleMessageId, getCategoryTitleFromMessageHistory, getSectionTitleFromMessageHistory, hasAnyMessages, isMessageUserSideArticle, randomId, spawnSystemMessage, spawnUserSystemMessage, spawnCategoryListMessage, spawnArticleCheckFailureMessage, spawnAlternativeActionsMessage, hasMessage, spawnUserSearchArticleMessage } from '../lib';
import { ActionSelectedEvent, ArticleAction, ArticleInteractionExternEvent, ArticleSystemOption, BaseEvent, BaseExternalEvent, BaseMessage, BaseSystemMessage, CancelSupportRequestExternEvent, ConnectToAgent, MessageConfiguration, MessageListItem, RollbackEvent, SystemListMessage, SystemOption, SystemOptionEvent, ArticleListWithOtherNavigationMessage, ArticleListNavigationEvent, PreSupportSelectionEvent, AlternativeActionSelectedEvent } from '../types';
import { MessageStoreApi, MessageUtil } from './messages';
import * as PleasedApi from '@/common/api/api.pleased';
import { Switcher, switcher } from './switcher';
import { isCategoryOption, isSectionOption, isArticleOption } from '../lib/option';
import { i18n } from '@/plugins/i18n';
import mitt, { Emitter } from 'mitt';
import { PrePromptConditions, conditionHelper, isPromise, toArr } from './ChatFramework.utils';
import { ChatFrameworkInterceptor, QueuedMessage, InterceptorMetadata, ChatFrameworkOptions, ChatFrameworkPreConfig, ChatFrameworkDynamicOptions } from './ChatFramework.types';

import { CHECKS } from './ChatMessageChecks.static';
import { resolveArticleAction } from './ChatMessageActions.static';

type ChatFrameworkEvents = {
  openArticle: { title: string, articleId: string|number; sourceMessageId: string};
  goToAction: ArticleAction;
  connect: {options: ConnectToAgent, message?: BaseMessage};
  endChat: void;
  'notify:restartChat': void;
  selectOption: {message:BaseMessage, selected: SystemOption};
}

export class ChatFramework {
  protected messageList!: MessageStoreApi;
  protected interceptors: ChatFrameworkInterceptor[] = [];

  protected lifecycle: 'created' | 'running' | 'paused' = 'created';
  protected emitter = mitt<ChatFrameworkEvents>();

  protected eventSwitch!: Switcher<BaseEvent>;
  protected externEventSwitch!: Switcher<BaseExternalEvent>;

  protected preConfig!: ChatFrameworkPreConfig;

  protected options: ChatFrameworkDynamicOptions = { canRunCheck: false }

  get $e (): Pick<Emitter<ChatFrameworkEvents>, 'on'| 'off'> {
    return this.emitter;
  }

  get status () {
    return this.lifecycle;
  }

  constructor (options: ChatFrameworkOptions) {
    this.messageList = options.messages;
    this.interceptors = this.interceptors.concat(options.interceptors || []);
    this.setupEventSwitch();
  }

  protected setupEventSwitch () {
    this.eventSwitch = switcher<BaseEvent>()
      .when(EventType.isOptionEvent, (event) => {
        const selected = event.payload.selected;
        this.emitter?.emit('selectOption', { selected, message: event.message });
        if (isCategoryOption(selected)) return this.onCategorySelected(event);
        if (isSectionOption(selected)) return this.onSectionSelected(event);
        if (isArticleOption(selected)) return this.onArticleSelected(event);
      })
      .when(EventType.isArticleListNavigationEvent, (event) => {
        const selected = event.payload.selected;
        if (isArticleOption(selected)) return this.onArticleSelected(event);
        if (selected === PredefinedOptionType.BROWSE_OTHER_ARTICLES) return this.openSectionsFromArticleList(event);
        // Directly connect through messages
        if (selected === PredefinedOptionType.ISSUE_NOT_LISTED) this.connectToAgentFromIssueNotListed(event);
      })
      .when(EventType.isRollbackEvent, this.rollback)
      .when(EventType.isActionSelectedEvent, this.executeAction)
      .when(EventType.isPreSupportPromptSelectionEvent, evt => {
        const selected = evt.payload.selected;

        if (selected === PredefinedOptionType.CONNECT_TO_SUPPORT) return this.connectToAgentFromEvent(evt);
        if (selected === PredefinedOptionType.BROWSE_OTHER_ARTICLES) return this.onExploreOtherArticlesFromPreSupportPrompt(evt);
        if (selected === PredefinedOptionType.RESTART_CHAT) return this.restartChat();
        if (selected === PredefinedOptionType.END_CHAT) return this.endConversationFlow();
        if (selected === PredefinedOptionType.POSITIVE_RESOLUTION) return this.positiveResolutionPreSupportPrompt(evt);
      })
      .when(EventType.isAlternativeActionEvent, this.navigateAlternativeAction)
      .none(e => console.error('[CF] Unhandled event passed', { e }));

    this.externEventSwitch = switcher<BaseExternalEvent>()
      .when(EventType.isArticleInteractionEvent, evt => {
        if (evt.payload.interaction === 'back') return this.onArticleBackToChatroom(evt);
        if (evt.payload.interaction === 'support-request') return this.onArticleConnect(evt);
        if (evt.payload.interaction === 'execute-action') return this.onArticleActionExecute(evt);
      })
      .when(EventType.isCancelSupportRequestEvent, this.onCancelSupportRequest)
      .none(e => console.error('[CF] Unhandled external event passed', { e }));
  }

  setPreConfig (config: ChatFrameworkPreConfig) {
    this.preConfig = config;
  }

  setOptions (options:{canRunCheck?: boolean}) {
    this.options = { ...this.options, ...options };
  }

  start () {
    const hasCategoryMessage = hasMessage(this.messageList.get(), { id: 'category-base' });
    if (hasCategoryMessage || this.lifecycle === 'running') return;
    if (!this.preConfig) {
      throw new Error('[CF] Cannot start without preConfig');
    }
    this.addCategoryMessage();
  }

  submit (event: BaseEvent) {
    this.eventSwitch.match(event);
  }

  submitExternalEvent = (event: BaseExternalEvent) => {
    this.externEventSwitch.match(event);
  }

  protected addCategoryMessage () {
    const config = this.preConfig;
    this.add(spawnCategoryListMessage({
      message: config.welcomeMessage,
      options: config.categories.map(c => ({ ...c, kind: SystemOptionType.CATEGORY })),
      id: 'category-base',
    }), { tags: ['from:system'] });
  }

  protected onCategorySelected = (event: SystemOptionEvent) => {
    const {
      payload: { selected: selectedCategory },
      message,
    } = event;

    const userSelectedCategoryMessage = this.getUserMessage(selectedCategory.title, event);

    // Mark it as selected.
    this.messageList.update(spawnSystemMessage<SystemListMessage>({ ...message, selectedOption: selectedCategory }));

    this.add(userSelectedCategoryMessage, { event, tags: ['from:visitor', 'opt.category'] });
    const categoryMessage = PleasedApi.getCategoryDetails
      .cached({ categoryId: selectedCategory.id })
      .then(({ data: { id: sectionId, categoryConfig, sections, articles } }) => {
        const articleOptions = articles.map(buildArticleOption).map(x => x.action ? { ...x, action: resolveArticleAction(x.action) } : x);
        const sectionOptions = sections.map((s) => ({ ...s, kind: SystemOptionType.SECTION }));

        // Fallback to old UI if there are no articles configured in the category.
        const config: MessageConfiguration = {
          // noneOfTheAbove: buildNoneOfTheAboveConfig(noneOfTheAboveConfig),
          connect: { ...buildConnectConfig(categoryConfig), category: selectedCategory.title },
          // resolution: buildResolution(categoryConfig),
        };

        return !articleOptions.length ? spawnSystemMessage<SystemListMessage>({
          type: MessageType.SECTION_LIST,
          id: `sections-${sectionId}-${randomId()}`,
          options: sectionOptions,
          config,
          message: categoryConfig.responseMessage || '',
          history: createMessageHistory(event, message),
        }) : spawnSystemMessage<ArticleListWithOtherNavigationMessage>({
          type: MessageType.ARTICLE_LIST_WITH_OTHER_NAVIGATION,
          id: `articles-or-sections-${sectionId}-${randomId()}`,
          options: articleOptions,
          config,
          sections: sectionOptions || [],
          elements: {
            browseArticles: Boolean(sectionOptions.length),
            issueNotListed: canConnectToAgent(config.connect),
          },
          message: categoryConfig.responseMessage || '',
          history: createMessageHistory(event, message),
        });
      }
      );

    this.add(categoryMessage, { event, tags: ['from:system', 'fetching', 'opt.category'] });
  }

  protected rollback = (event: RollbackEvent) => {
    MessageUtil.removeMessagesAfter(this.messageList, event.message);
  }

  protected openConnectFromMyIssueNotSelected (event: BaseEvent) {
    // Need to know the category.
    const { message: evtMessage } = event;
    const message = evtMessage as ArticleListWithOtherNavigationMessage;

    this.connectArticleWithAcknowledgement({
      supportOptions: message.config?.connect as ConnectToAgent,
      event,
      history: createMessageHistory(event, message),
    });
  }

  protected openSectionsFromArticleList = (event: BaseEvent) => {
    // Need to know the category.
    const { message: evtMessage } = event;
    const message = evtMessage as ArticleListWithOtherNavigationMessage;

    const sections = message.sections;
    const userResponse = this.getUserMessage(i18n.t('chat.v2.user.section.other_articles') as string, event);
    this.add(userResponse, { event, tags: ['from:visitor'] });

    const updatedMessage = { ...message, selectedOption: PredefinedOptionType.BROWSE_OTHER_ARTICLES };

    this.messageList.update(updatedMessage);

    const sectionMessage = spawnSystemMessage<SystemListMessage>({
      type: MessageType.SECTION_LIST,
      options: sections,
      config: evtMessage.config,
      message: i18n.t('chat.v2.sys.section.more_sections_title') as string,
      history: createMessageHistory(event, message),
    });

    this.add(sectionMessage, { event, tags: ['from:system'] });
  }

  protected getUserMessage (helpTopic: string, event?: BaseEvent) {
    return spawnUserSystemMessage({
      sendToChat: true,
      message: i18n.t('chat.user.response.message.text', { name: helpTopic }) as string,
      interactiveMessageParts: [
        i18n.t('chat.user.response.message.part', { name: helpTopic }) as string,
        helpTopic,
      ],
      ...(event ? { history: createMessageHistory(event, event.message) } : {})
      ,
    });
  }

  protected onSectionSelected = (event: SystemOptionEvent) => {
    const {
      payload: { selected: section },
      message,
    } = event;

    const userMessage = this.getUserMessage(section.title, event);
    this.add(userMessage, { event, tags: ['from:visitor', 'opt.section'] });

    // const isFirstLevelSection = message.config && !message.config.connect;

    const parentConfig = message.config?.connect;

    const loadingSectionMessage = PleasedApi.getSectionDetails
      .cached({ sectionId: section.id })
      .then(({ data: { sectionConfig, sections, articles } }) => {
        const responseMessage = sectionConfig.responseMessage || '';

        const mappedSections: SystemOption[] = (sections || []).map((s) => ({ ...s, kind: SystemOptionType.SECTION }));
        const mappedArticles: SystemOption[] = (mappedSections.length ? [] : articles).map((a) => (buildArticleOption({
          ...a,
          category: parentConfig?.category || '',
          section: section.title,
        })));

        const options = mappedSections.concat(mappedArticles);

        const config = sectionConfig.overrideParentConfig ? buildConnectConfig(sectionConfig) : parentConfig;

        const messageToShow = spawnSystemMessage<SystemListMessage>({
          type: MessageType.HYBRID_LIST,
          config: { ...message.config, connect: { ...config!, section: section.title } },
          options,
          message: responseMessage,
          history: createMessageHistory(event, message, userMessage),
        });
        return messageToShow;
      });

    this.add(loadingSectionMessage, { event, tags: ['from:system', 'fetching', 'opt.section'] });
  }
  protected onArticleSelected = async (event: ArticleListNavigationEvent|SystemOptionEvent) => {
    const { payload: { selected }, message } = event;
    const option = selected as ArticleSystemOption;
    const updatedMessage = { ...message, selectedOption: selected };

    this.messageList.update(updatedMessage);

    this.addArticleUserMessage(option, event);

    // There are 2 flows here, if the user has a check, or go to article page.

    if (!option.checks || ItemType.isArticleCheckFailureMessage(message)) {
      // No checks necessary, open the article page, but we need to check triggers as well.
      this.emitter.emit('openArticle', { articleId: option.id, title: option.title, sourceMessageId: message.id as string });
      return;
    }
    // If checks are present
    const nextMessage = this.runCheck({ check: 'KYC' }).then(checkPassed => {
      if (checkPassed) {
        this.emitter.emit('openArticle', { articleId: option.id, title: option.title, sourceMessageId: message.id as string });
        // Don't queue any message. Returning empty array here will ensure that no messages are queued.
        return [];
      }
      // Check not passed. show the corresponding action.
      const slots = CHECKS.KYC.slots();
      return spawnArticleCheckFailureMessage({
        message: slots.message,
        article: { ...option },
        messageSlots: {
          afterArticle: slots.actionMessage,
        },
        config: {
          connect: message.config?.connect,
          action: option.action,
        },
      });
    });

    this.add(nextMessage, { event, tags: ['fetching', 'from:system', 'check.running'] });
  }

  protected addArticleUserMessage (option: ArticleSystemOption, event: BaseEvent|BaseExternalEvent) {
    const conversation = this.messageList.get().filter(m => !ItemType.isPreSupportPromptMessage(m));

    const lastMessage = conversation[conversation.length - 1];
    const userSideArticleId = generateUserSideArticleMessageId(option.id);
    const isArticleMessageAlreadyExists = isMessageUserSideArticle(lastMessage?.id, option.id);

    let spawnMessageFactory = spawnUserSystemMessage;
    if (EventType.isArticleInteractionEvent(event) && !event.payload.sourceMessageId) {
      // if there is no source message id, this message is probably from article search.
      spawnMessageFactory = spawnUserSearchArticleMessage;
    }

    if (!isArticleMessageAlreadyExists) {
      const userMessage = spawnMessageFactory({
        id: userSideArticleId,
        message: i18n.t('chat.user.response.message.text', { name: option.title }) as string,
        interactiveMessageParts: [i18n.t('chat.user.response.message.part') as string, option.title],
        history: EventType.isExternalEvent(event) ? this.generateArticleMessageHistory(event as ArticleInteractionExternEvent) : createMessageHistory(event, event.message),
      });
      // Looks like you have checked out our article, you can create the new
      this.add(userMessage, { event, tags: ['from:visitor', 'opt.article'] });
    }
  }

  protected generateArticleMessageHistory (event: ArticleInteractionExternEvent) {
    const sourceMessage = event.payload.sourceMessageId ? this.messageList.get().find(m => m.id === event.payload.sourceMessageId) as BaseMessage || null : null;
    const history = sourceMessage ? createMessageHistory(event, sourceMessage) : undefined;
    return history;
  }

  protected connectToAgentFromIssueNotListed (event: ArticleListNavigationEvent) {
    const hardcodedConnectConfigParams = { showDescriptionBox: true };
    const updatedEvent = {
      ...event,
      message: {
        ...event.message,
        config: { ...event.message.config, connect: { ...event.message.config?.connect!, ...hardcodedConnectConfigParams },
        },
      },
    };

    this.connectToAgentFromEvent(updatedEvent);
  }

  protected connectToAgentFromEvent (event: ArticleListNavigationEvent|PreSupportSelectionEvent|AlternativeActionSelectedEvent) {
    const KNOWN_SELECTION_VALIDATION = [PredefinedOptionType.CONNECT_TO_SUPPORT, PredefinedOptionType.ISSUE_NOT_LISTED];
    if (!KNOWN_SELECTION_VALIDATION.includes(event.payload.selected as any)) {
      console.warn('[CF] The connect to agent from event is coming from unrecognized event', { event });
    }

    const { message, payload: { selected } } = event;
    this.updateMessageSelection(message, selected);

    this.connectArticleWithAcknowledgement({
      history: createMessageHistory(event, event.message),
      supportOptions: event.message.config?.connect!,
      event,
    });
  }

  protected positiveResolutionPreSupportPrompt (evt: PreSupportSelectionEvent) {
    this.updateMessageSelection(evt.message, PredefinedOptionType.POSITIVE_RESOLUTION);
    this.add(spawnUserSystemMessage({ message: i18n.t('chat.v2.user.my_issue_resolved') as string }));
    const message = spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PROMPT_BARE,
      message: i18n.t('chat.v2.sys.glad_to_help') as string,
      history: createMessageHistory(evt, evt.message),
    });
    this.add(message, { event: evt, tags: ['from:system'] });
  }

  protected onExploreOtherArticlesFromPreSupportPrompt (evt: PreSupportSelectionEvent) {
    this.updateMessageSelection(evt.message, PredefinedOptionType.BROWSE_OTHER_ARTICLES);
    const message = spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_PROMPT_BARE,
      message: '',
      history: createMessageHistory(evt, evt.message),
    });
    this.add(message, { event: evt, tags: ['from:system'] });
  }

  protected updateMessageSelection (message: BaseSystemMessage, selected: string|SystemOption) {
    const updatedMessage = { ...message, selectedOption: selected };
    this.messageList.update(updatedMessage);
  }

  protected connectArticleWithAcknowledgement = ({ history, supportOptions, event }: ({ supportOptions: ConnectToAgent, history?: BaseMessage['history'], event?: BaseEvent|BaseExternalEvent})) => {
    const acknowledgement = spawnSystemMessage({
      type: MessageType.PRE_SUPPORT_ACKNOWLEDGEMENT,
      message: i18n.t('chat.v2.sys.lets_connect_to_agent') as string,
      history,
      config: { connect: supportOptions },
    });

    this.add(acknowledgement, { event, tags: ['article.connect'] });

    this.connectToAgentFromMessage(acknowledgement);
  }

  protected onArticleConnect = (event: ArticleInteractionExternEvent) => {
    const { supportOptions, article: sourceArticleOption } = event.payload;
    this.addArticleUserMessage(sourceArticleOption, event);

    const history = this.generateArticleMessageHistory(event);
    this.connectArticleWithAcknowledgement({ history, supportOptions, event });
  }

  protected onArticleActionExecute (event: ArticleInteractionExternEvent) {
    console.error('[CF] Action executed from article pages (external actions outside chat messages) are not handled by the CF');
  }

  protected onArticleBackToChatroom (event: ArticleInteractionExternEvent) {
    // Can be an external event. Not coming from message.
    // There are 3 messages here to add.
    // 1. Intermediate resolution.
    // 2. user side article search.
    const { supportOptions, article: sourceArticleOption } = event.payload;
    this.addArticleUserMessage(sourceArticleOption, event);

    // Article has connect, but no action.

    const sourceMessage = event.payload.sourceMessageId ? this.messageList.get().find(m => m.id === event.payload.sourceMessageId) as BaseMessage || null : null;
    const history = sourceMessage ? createMessageHistory(event, sourceMessage) : undefined;

    const hasAction = Boolean(sourceArticleOption.action?.action);
    const hasSupport = canConnectToAgent(supportOptions);

    const connect: ConnectToAgent|undefined = supportOptions ? {
      ...supportOptions,
      category: supportOptions.category || sourceArticleOption?.category || (history ? getCategoryTitleFromMessageHistory(history) || '' : ''),
      section: supportOptions.section || sourceArticleOption?.section || (history ? getSectionTitleFromMessageHistory(history) || '' : ''),
    } : undefined;

    const nextMessage = switcher<PrePromptConditions, BaseSystemMessage>({ hasAction, hasSupport })
      .when(conditionHelper({ hasAction: true, hasSupport: true }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_ACTION_WITH_SUPPORT,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        config: {
          action: sourceArticleOption.action,
          connect,
        },
        history,
      }))
      .when(conditionHelper({ hasAction: false, hasSupport: false }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_BARE,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        history,
      }))
      .when(conditionHelper({ hasAction: false, hasSupport: true }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_SUPPORT_ONLY,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        config: {
          connect,
        },
        history,
      }))
      .when(conditionHelper({ hasAction: true, hasSupport: false }), () => spawnSystemMessage({
        type: MessageType.PRE_SUPPORT_PROMPT_ACTION_ONLY,
        message: i18n.t('chat.v2.sys.back_from_article') as string,
        config: {
          action: sourceArticleOption.action,
        },
        history,
      })).match();

    if (!nextMessage) {
      console.error('[CF]: Not able to determine next message when back from the article page.');
    }

    // We want to safely replace the message.
    this.add({ ...nextMessage, id: `${nextMessage.type}--(articleID=${sourceArticleOption.id})` }, { event, tags: ['from:system'] });
  }

  protected executeAction = (event: ActionSelectedEvent) => {
    this.emitter.emit('goToAction', event.payload.action);
  }

  protected async runCheck ({ check }:{check: string}) {
    if (!this.options.canRunCheck) return true;
    return CHECKS.KYC.check();
  }

  protected navigateAlternativeAction = (event: AlternativeActionSelectedEvent) => {
    const { payload: { selected } } = event;
    if (selected === PredefinedOptionType.RESTART_CHAT) return this.restartChat();
    if (selected === PredefinedOptionType.END_CHAT) return this.endConversationFlow();
    if (selected === PredefinedOptionType.CONNECT_TO_SUPPORT) return this.connectToAgentFromEvent(event);
  }

  protected onCancelSupportRequest = (event: CancelSupportRequestExternEvent) => {
    const message = spawnUserSystemMessage({
      message: i18n.t('chat.user.issue.cancel_request') as string,
    });

    this.add(message, { event, tags: ['cancel.support'] });
    const cancelSupportMessage = spawnAlternativeActionsMessage({ config: { connect: event.payload.supportOptions } });
    this.add(cancelSupportMessage, { event, tags: ['cancel.support'] });
  }

  protected connectToAgentFromMessage (message: BaseSystemMessage) {
    if (!message.config?.connect) {
      console.error('[CF] Message does not have a connect to agent config.', { message });
      return;
    }
    if (!canConnectToAgent(message.config?.connect)) {
      console.warn('[CF] Message has incorrect connect to agent config. They cannot connect to agent with this config.', { message });
      return;
    }
    this.emitter.emit('connect', { options: message.config.connect, message });
  }

  protected endConversationFlow () {
    this.add(
      [
        spawnUserSystemMessage({ message: i18n.t('chat.v2.user.lets_end_chat') as string }),
        spawnSystemMessage({ message: i18n.t('chat.sys.goodbye') as string, type: MessageType.DEFAULT }),
      ],
    );
    this.emitter.emit('endChat');
  }

  protected restartChat () {
    this.emitter.emit('notify:restartChat');
    this.messageList.clear();
    this.addCategoryMessage();
  }

  protected add (messages: QueuedMessage, event?: InterceptorMetadata) {
    const finalMessages = this.interceptors.reduce((acc: QueuedMessage, interceptor) => interceptor.intercept(acc, event) || acc, messages);
    if (!isPromise(finalMessages)) return this.messageList.addSafe(...toArr(finalMessages));
    return MessageUtil.addAsync(this.messageList, finalMessages);
  }
}
